import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ORGANIZATION_DISPLAY_NAME } from '../config/branding';
import { authAPI } from '../services/api';
import i18n from '../i18n';
import { I18N_UI_LANGUAGE_OPTIONS, normalizeToSupportedUiLanguage } from '../constants/uiLanguages';
import { syncUiLanguageToMemberProfile } from '../utils/syncUiLanguageToMemberProfile';
import {
  buildFirstLoginNavigateState,
  getPostFirstLoginDestination,
} from '../utils/firstLoginFlow';

/**
 * Obligatoire après création d’un compte avec mot de passe plateforme par défaut.
 */
const FirstLoginChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnFallback = location.state?.returnFallback || '/dashboard';
  const { t } = useTranslation('common');
  const { token, user, loading, loginWithToken, logout } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [uiLanguage, setUiLanguage] = useState(() => normalizeToSupportedUiLanguage(i18n.language));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setUiLanguage(normalizeToSupportedUiLanguage(i18n.language));
  }, [i18n.language]);

  useEffect(() => {
    if (!loading && !token) {
      navigate('/login', { replace: true });
    }
  }, [loading, token, navigate]);

  useEffect(() => {
    if (!loading && token && user && !user.must_change_password) {
      navigate(getPostFirstLoginDestination(user, { fallback: returnFallback }), {
        replace: true,
        state: buildFirstLoginNavigateState(user, { fallback: returnFallback }),
      });
    }
  }, [loading, token, user, navigate, returnFallback]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirm) {
      setError('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }
    setSubmitting(true);
    try {
      const projectId = localStorage.getItem('project_id');
      const res = await authAPI.changePassword(oldPassword, newPassword, projectId);
      const { access_token, user: u } = res.data;
      loginWithToken(access_token, u);
      await syncUiLanguageToMemberProfile(u, uiLanguage);
      navigate(getPostFirstLoginDestination(u, { fallback: returnFallback }), {
        replace: true,
        state: buildFirstLoginNavigateState(u, { fallback: returnFallback }),
      });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Impossible de mettre à jour le mot de passe.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="mx-auto flex min-h-screen w-full max-w-lg flex-col px-4 py-8 sm:px-6 md:max-w-xl">
        <Link
          to="/"
          className="mb-8 flex flex-col items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-entrea-royal focus-visible:ring-offset-2"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-entrea-ink to-entrea-royal shadow-md">
            <svg className="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
          </div>
          <div className="text-center">
            <span className="text-2xl font-bold tracking-tight text-slate-900">{ORGANIZATION_DISPLAY_NAME}</span>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Uruganda Platform</p>
          </div>
        </Link>

        <div className="w-full rounded-xl border border-slate-200 bg-white px-6 py-8 shadow-sm sm:px-10 sm:py-10">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Nouveau mot de passe</h1>
          <p className="text-gray-600 text-sm mb-6">
            Votre compte utilise un <strong>mot de passe initial</strong>. Choisissez un{' '}
            <strong>mot de passe personnel</strong> avant de continuer.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('firstLogin.defaultLanguage')}
            </label>
            <select
              value={uiLanguage}
              onChange={(e) => setUiLanguage(normalizeToSupportedUiLanguage(e.target.value))}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-1 focus:ring-entrea-royal focus:border-entrea-sky bg-white"
            >
              {I18N_UI_LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-gray-500">{t('firstLogin.defaultLanguageHint')}</p>
          </div>
          {error && (
            <div className="rounded-lg bg-red-50 text-red-800 text-sm px-4 py-3" role="alert">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe actuel</label>
            <input
              type="password"
              autoComplete="current-password"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-1 focus:ring-entrea-royal focus:border-entrea-sky"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-1 focus:ring-entrea-royal focus:border-entrea-sky"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-1 focus:ring-entrea-royal focus:border-entrea-sky"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 rounded-lg bg-entrea-royal text-white font-semibold hover:bg-entrea-navy disabled:opacity-50"
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer et continuer'}
          </button>
        </form>
        <button
          type="button"
          onClick={() => logout()}
          className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700"
        >
          Se déconnecter
        </button>
        </div>

        <footer className="mt-8 text-center text-xs text-slate-500">
          <p className="mb-1">
            © {new Date().getFullYear()} {ORGANIZATION_DISPLAY_NAME} · Uruganda Platform
          </p>
          <p className="space-x-2">
            <Link to="/" className="hover:text-slate-700 transition-colors">Conditions</Link>
            <span>·</span>
            <Link to="/" className="hover:text-slate-700 transition-colors">Confidentialité</Link>
            <span>·</span>
            <Link to="/" className="hover:text-slate-700 transition-colors">Assistance</Link>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default FirstLoginChangePassword;
